import React, { useContext, useState, useEffect } from 'react';
import { Search, Eye, Edit, Trash2, Plus, Filter, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import activityService from '../../../services/activityService';
import Pagination from '../../../components/Pagination';

export default function ManageActivities() {
  const {hasRole, authTokens, user } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    activity_name: '',
    status: '',
    ordering: 'newest'
  });

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });
  const [showFiltersSection, setShowFiltersSection] = useState(false);

  // Fetch user's activities on component mount
  useEffect(() => {
    fetchMyActivities();
  }, [pagination.page, pagination.page_size]);

  const fetchMyActivities = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const pagination_data = {
        page: pagination.page,
        page_size: pagination.page_size,
      };
      const result = await activityService.getMyActivities(authTokens.access, filters, pagination_data);
      
      if (result.success) {
        setActivities(result.data.results);

        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        setError(result.error);
        showError(result.error || 'Erreur lors du chargement des activités');
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
      setError('Erreur de connexion');
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };



  const canAddActivity = () => {
    return hasRole("RiskManager") || hasRole("RiskIdentifier");
  };

  const canEditActivity = () => {
    return hasRole("RiskManager") || hasRole("RiskIdentifier");
  };

  const canDeleteActivity = () => {
    return hasRole("Admin");
  };

  const canViewAllActivities = () => {
    return hasRole("Admin") || hasRole("RiskManager");
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 // Reset to first page
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    // Update the activity_name filter and fetch
    setFilters(prev => ({
      ...prev,
      activity_name: searchQuery
    }));
  };

  const applyFilters = () => {
    fetchMyActivities(); // Re-fetch with current filters
  };

  const clearFilters = () => {
    setFilters({
      activity_name: '',
      status: '',
      ordering: 'newest'
    });
    setSearchQuery('');
  };

  // Apply filters when they change
  useEffect(() => {
    if (!loading) {
      fetchMyActivities();
    }
  }, [filters]);

  const handleEditActivity = (activity) => {
    navigate(`/dashboard/activities/edit/${activity.id}`, { state: { activity } });
  };

  const handleViewActivity = (activity) => {
    navigate(`/dashboard/activities/details/${activity.id}`);
  };

  const handleDeleteActivity = async (activity) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'activité "${activity.activity_name}" ?`)) {
      try {
        const result = await activityService.deleteActivity(activity.id, authTokens.access);
        
        if (result.success) {
          showSuccess('Activité supprimée avec succès');
          fetchMyActivities(); // Refresh the list
        } else {
          showError(result.error || 'Erreur lors de la suppression');
        }
      } catch (error) {
        console.error('Error deleting activity:', error);
        showError('Erreur lors de la suppression');
      }
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    if (status?.toLowerCase() === 'active') {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (status?.toLowerCase() === 'inactive') {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
    return `${baseClasses} bg-gray-100 text-gray-800`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        {/* Left side - Title only */}
        <h2 className="text-xl font-semibold text-gray-900">Mes Activités</h2>

        {/* Right side - Buttons */}
        <div className="flex items-center space-x-3">
          {canViewAllActivities() && (
            <Link
              to="/dashboard/activities/all"
              className="flex items-center px-3 py-1.5 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
            >
              Voir Tous
            </Link>
          )}

          {canAddActivity() && (
            <Link
              to="/dashboard/activities/add"
              className="flex items-center px-3 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              <Plus className="h-3 w-3 mr-1.5" />
              Ajouter
            </Link>
          )}
        </div>
      </div>


      {/* Search and Filter Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher une activité..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="flex items-center justify-center px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors duration-200 sm:px-4"
              >
                <Search className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Rechercher</span>
              </button>
              <button
                onClick={() => setShowFiltersSection(!showFiltersSection)}
                className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200 sm:px-3"
              >
                <Filter className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Filtres</span>
              </button>
            </div>
          </div>

          {/* Filters Section */}
          {showFiltersSection && (
            <div className="border-t border-gray-200 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">Tous</option>
                    <option value="Active">Actif</option>
                    <option value="Inactive">Inactif</option>
                  </select>
                </div>

                {/* Ordering Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Ordre
                  </label>
                  <select
                    value={filters.ordering}
                    onChange={(e) => handleFilterChange('ordering', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="newest">Plus récent</option>
                    <option value="oldest">Plus ancien</option>
                  </select>
                </div>

                {/* Filter Actions */}
                <div className="flex items-end space-x-2">
                  <button
                    onClick={applyFilters}
                    className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors duration-200"
                  >
                    Appliquer
                  </button>
                  <button
                    onClick={clearFilters}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors duration-200"
                  >
                    Effacer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-500">Chargement des activités...</div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500">
              <p className="text-lg font-medium">Erreur de chargement</p>
              <p className="mt-1">{error}</p>
              <button
                onClick={fetchMyActivities}
                className="mt-3 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
              >
                Réessayer
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date de création
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activities.map((activity) => (
                    <tr key={activity.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {activity.activity_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {activity.description || 'Aucune description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(activity.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(activity.status)}>
                          {activity.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewActivity(activity)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors duration-200"
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {canEditActivity() && (
                            <button
                              onClick={() => handleEditActivity(activity)}
                              className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-colors duration-200"
                              title="Modifier"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}

                          {/* {canDeleteActivity() && (
                            <button
                              onClick={() => handleDeleteActivity(activity)}
                              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors duration-200"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )} */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages}
              totalItems={pagination.count}
              pageSize={pagination.page_size}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              loading={loading}
              itemName="Activities" 
            />

            {/* Empty State */}
            {activities.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  <p className="text-lg font-medium">Aucune activité trouvée</p>
                  <p className="mt-1">
                    {filters.activity_name || filters.status 
                      ? 'Essayez de modifier vos filtres de recherche.' 
                      : 'Commencez par créer votre première activité.'
                    }
                  </p>
                  {canAddActivity() && (
                    <Link
                      to="/dashboard/activities/add"
                      className="mt-4 inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors duration-200"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Créer une activité
                    </Link>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}